import { jwtDecode } from 'jwt-decode';
import memoizeOne from 'memoize-one';

import { currentJwt } from '@src/utils/jwtHelpers';

let isInitialized = false;

const getUserId = (jwtObj) => {
    if (!jwtObj) {
        return '';
    }

    if (jwtObj.agent_code) {
        return jwtObj.agent_code;
    }

    if (jwtObj.producer_id) {
        return jwtObj.producer_id;
    }

    if (jwtObj.employee_id) {
        return jwtObj.employee_id;
    }

    return jwtObj.uuid;
};

const getData = (jwt) => {
    let decodedJwt = null;
    if (jwt) {
        decodedJwt = jwtDecode(jwt);
    }

    if (!decodedJwt) return {};

    return {
        accessLevel: decodedJwt.access_level,
        salesChannel: decodedJwt.sales_channel || null,
        userId: getUserId(decodedJwt),
    };
};

const memoizedData = memoizeOne(getData);

export const trackEvent = (eventName, params = {}) => {
    if (typeof window === 'undefined') return;

    if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
    }

    if (window.dataLayer) {
        window.dataLayer.push({
            event: eventName,
            ...params,
        });
    }
};

export const trackContentView = ({ id, resourceType, title, page_path }) => {
    if (!id) return;

    trackEvent('content_view', {
        content_id: String(id),
        content_type: resourceType || 'Content',
        content_title: title || '',
        page_path: page_path || (typeof window !== 'undefined' ? window.location.pathname : ''),
    });
};

export const sendPageView = (location) => {
    if (!location || typeof window === 'undefined') return;

    const pagePath = location.search ? `${location.pathname}${location.search}` : location.pathname;

    if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
            page_title: document.title,
            page_location: window.location.href,
            page_path: pagePath,
        });
    }

    if (window.dataLayer) {
        window.dataLayer.push({
            event: 'pageview',
            pagePath,
            pageTitle: document.title,
        });
    }
};

export const trackGaPageview = sendPageView;

export default function googleAnalytics(historyOrUserInfo, maybeUserInfo) {
    if (typeof window === 'undefined') {
        return;
    }

    let history = null;
    let userInfo = null;

    if (historyOrUserInfo && (historyOrUserInfo.listen || historyOrUserInfo.location)) {
        history = historyOrUserInfo;
        userInfo = maybeUserInfo;
    } else {
        userInfo = historyOrUserInfo;
    }

    const jwt = currentJwt();

    let accessLevel, salesChannel, userId;

    if (userInfo) {
        accessLevel = userInfo.accessLevel;
        salesChannel = userInfo.salesChannel;
        userId = userInfo.agentCode || userInfo.producerId || userInfo.employeeId || userInfo.uuid;
    } else {
        const data = memoizedData(jwt);
        accessLevel = data.accessLevel;
        salesChannel = data.salesChannel;
        userId = data.userId;
    }

    if (!jwt) {
        return;
    }

    if (accessLevel === 'admin') {
        return;
    }

    const measurementId = process.env.GA_MEASUREMENT_ID;
    if (!measurementId) {
        return;
    }

    if (isInitialized) {
        return;
    }

    window.dataLayer = window.dataLayer || [];
    function gtag() {
        window.dataLayer.push(arguments);
    }
    window.gtag = window.gtag || gtag;

    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`);
    if (!existingScript) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        document.head.appendChild(script);
    }

    window.gtag('js', new Date());

    const configOptions = {
        send_page_view: false,
    };

    if (userId) {
        configOptions.user_id = userId;
    }

    window.gtag('config', measurementId, configOptions);

    const userProperties = {};
    if (salesChannel) {
        userProperties.sales_channel = salesChannel;
    }
    if (accessLevel) {
        userProperties.access_level = accessLevel;
    }
    if (Object.keys(userProperties).length > 0) {
        window.gtag('set', 'user_properties', userProperties);
    }

    isInitialized = true;

    if (history && history.location) {
        // Send initial pageview for the current location
        sendPageView(history.location);

        // Listen to subsequent route transitions in React Router
        if (typeof history.listen === 'function') {
            history.listen((location) => {
                sendPageView(location);
            });
        }
    }
}
